import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import { Button, Conditional, Else, Heading, If, mappedArray, Row } from "@hex-insights/core";
import {
	PermissionFilterForm,
	PermissionFilterFormState,
	PermissionFilterFormUtils,
	PermissionIndexQuery,
	PermissionOrderField,
	PermissionOrderForm,
	PermissionOrderFormState,
	PermissionOrderFormValues,
	PermissionPaginationForm,
	usePermissionIndexQuery,
} from "@hex-insights/roadium.shared";
import { InternalLink, useActivePageRegistration } from "@hex-insights/router";
import { Table } from "@hex-insights/tables";
import { Main } from "../../../../Components";
import { permissionHubDetailPageInfo } from "../DetailPage/pageinfo";
import { permissionHubIndexPageInfo } from "./pageinfo";

const columns: (keyof PermissionIndexQuery["permissionConnection"]["edges"][0]["node"])[] = [
	"name",
	"family",
	"action",
	"level",
];

function orderFieldToColumn(field: PermissionOrderField | null) {
	if (field === null) {
		return "id";
	}
	return PermissionOrderFormValues.orderFieldToModelField[field];
}

function columnToOrderField(column: string) {
	if (column === "id") {
		return null;
	}
	return PermissionOrderFormValues.modelFieldToOrderField[column];
}

export function PermissionHubIndexPage() {
	useActivePageRegistration(permissionHubIndexPageInfo);

	const filterFormState = PermissionFilterFormState.useFilterFormStateWithQueryStateSync();
	const orderFormState = PermissionOrderFormState.useOrderFormStateWithQueryStateSync();
	const paginationFormState = IndexForms.usePaginationFormStateWithQueryStateAndLocalStorageSync();

	const filterInputs = IndexForms.useFilterInput(filterFormState.formValues, PermissionFilterFormUtils.toFilterInputs);
	const orderInput = IndexForms.useOrderInput(orderFormState.formValues);
	const queryVariables = IndexForms.useQueryVariables(
		filterInputs,
		orderInput,
		paginationFormState.formValues,
		paginationFormState.formSetFunctions.cursor,
	);
	const { loading, data, error } = usePermissionIndexQuery({ variables: queryVariables });

	const clearFilters = React.useCallback(() => {
		PermissionFilterFormUtils.clearFilters(filterFormState);
	}, [filterFormState]);

	const numActiveFilters = PermissionFilterFormUtils.countActiveFilters(filterFormState.formValues);
	const [paginationInfo, setPaginationInfo] = IndexForms.usePaginationInfo();

	const tableSortingProps = IndexForms.useTableSortingFromOrderForm(
		orderFormState,
		orderFieldToColumn,
		columnToOrderField,
	);

	const numFillerRows = Math.max(
		(paginationFormState.formValues.resultsPerPage ?? 0) - (data?.permissionConnection.edges.length ?? 0),
		0,
	);

	return (
		<Main>
			<Row justify="space-between" align="center" style={{ margin: "var(--heading--level-1---margin)" }}>
				<Heading level={1} noMargin>
					Permissions
				</Heading>
			</Row>

			<Row
				justify="spaced-start"
				align="center"
				horizontalSpacing="0.25rem"
				withVerticalSpacing
				overflow="wrap"
				style={{ marginBottom: "0.75rem" }}
			>
				<PermissionFilterForm.Search formState={filterFormState} />
				<PermissionFilterForm.FilterController formState={filterFormState} numActiveFilters={numActiveFilters} />
				<If condition={numActiveFilters > 0}>
					<Button variant="link" size="small" onClick={clearFilters}>
						Clear
					</Button>
				</If>
				<PermissionOrderForm.OrderController formState={orderFormState} />
			</Row>

			<Table.Container className="hub__index-page__table__container">
				<Table
					columns={columns}
					minColumnWidth="10rem"
					scrollSyncID={paginationFormState.formValues.cursor}
					{...tableSortingProps}
					className="hub__index-page__table"
				>
					<Table.Header className="hub__index-page__table__header">
						<Table.Row>
							<Table.Heading column="name" noSort>
								Name
							</Table.Heading>
							<Table.Heading column="family">Family</Table.Heading>
							<Table.Heading column="action">Action</Table.Heading>
							<Table.Heading column="level">Level</Table.Heading>
						</Table.Row>
					</Table.Header>

					<Table.Body className="hub__index-page__table__body">
						<Conditional>
							<If condition={loading}>
								<Table.Row className="hub__index-page__table__row">
									<Table.Cell column="name" colspan={columns.length}>
										Loading...
									</Table.Cell>
								</Table.Row>
								{mappedArray((paginationFormState.formValues.resultsPerPage ?? 1) - 1, (i) => (
									<Table.FillerRow
										key={i}
										className="hub__index-page__table__row hub__index-page__table__row--filler"
									/>
								))}
							</If>

							<Else>
								{data?.permissionConnection.edges.map((e) => (
									<Table.Row key={e.node.id} className="hub__index-page__table__row">
										<Table.Cell column="name" oneLine>
											<InternalLink to={permissionHubDetailPageInfo.to(e.node.id)}>{e.node.name}</InternalLink>
										</Table.Cell>
										<Table.Cell column="family" oneLine>
											{e.node.family}
										</Table.Cell>
										<Table.Cell column="action" oneLine>
											{e.node.action}
										</Table.Cell>
										<Table.Cell column="level" oneLine>
											{e.node.level}
										</Table.Cell>
									</Table.Row>
								))}
								{mappedArray(numFillerRows, (i) => (
									<Table.FillerRow
										key={i}
										className="hub__index-page__table__row hub__index-page__table__row--filler"
									/>
								))}
							</Else>
						</Conditional>
					</Table.Body>
				</Table>

				<Table.Toolbar>
					<Row justify="space-between" align="center">
						<PermissionPaginationForm.PaginationForm
							formState={paginationFormState}
							filters={filterInputs}
							order={orderInput}
							setPaginationInfo={setPaginationInfo}
						/>

						<span>
							<Conditional>
								<If condition={loading}>Loading...</If>
								<If condition={!!error}>Something went wrong.</If>
								<If condition={data?.permissionConnection.edges.length === 0}>No results found.</If>
								<Else>{IndexForms.getPaginationDescription(paginationInfo)}</Else>
							</Conditional>
						</span>
					</Row>
				</Table.Toolbar>
			</Table.Container>
		</Main>
	);
}
